import { clsx } from 'clsx';
import { Loader2, Send } from 'lucide-react';
import type { PendingSend } from '@/features/tickets/useUndoSend';

interface UndoSendBarProps {
  items: PendingSend[];
  /** Current time, ticked by the owner while a countdown runs. */
  now: number;
  onUndo: (operationId: string) => void;
  /** Screen-reader announcement (start / sent / cancelled), not the per-second tick. */
  announcement?: string;
}

/**
 * Outlook-style "Sending… Undo" strip above the composer — one row per email
 * reply still inside its undo window. Non-blocking: the agent can keep working
 * (and even send more replies) while it counts down.
 */
export function UndoSendBar({ items, now, onUndo, announcement }: UndoSendBarProps) {
  return (
    <>
      {/* Polite, and only on state changes — a ticking countdown would flood a screen reader. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {items.length > 0 && (
        <div
          className="flex-none space-y-2 border-t border-[var(--co-line)] bg-[var(--co-surface)] px-4 pt-2.5 sm:px-6"
          data-testid="undo-send-bar"
        >
          {items.map((item) => {
            const remainingMs = Math.max(0, item.deadline - now);
            const seconds = Math.ceil(remainingMs / 1000);
            const total = Math.max(1, item.undoSeconds * 1000);
            const fraction = item.phase === 'counting' ? Math.min(1, remainingMs / total) : 0;
            const canUndo = item.phase === 'counting' && remainingMs > 0;

            return (
              <div
                key={item.operationId}
                role="group"
                aria-label={`Email to ${item.recipientsLabel}`}
                className="relative overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]"
              >
                <div className="flex items-center gap-3 px-3 py-2">
                  {item.phase === 'counting' ? (
                    <Send aria-hidden className="h-3.5 w-3.5 flex-none text-[var(--co-brass-ink)]" />
                  ) : (
                    <Loader2 aria-hidden className="h-3.5 w-3.5 flex-none animate-spin text-[var(--co-ink-4)]" />
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] leading-5 text-[var(--co-ink-2)]">
                      {item.phase === 'counting' && (
                        <>
                          Email will be sent in{' '}
                          <span className="font-mono font-semibold tabular-nums text-[var(--co-ink)]" aria-hidden>
                            {seconds}s
                          </span>
                          <span className="sr-only">{seconds} seconds</span>
                        </>
                      )}
                      {item.phase === 'undoing' && 'Cancelling…'}
                      {item.phase === 'sending' && 'Sending…'}
                    </p>
                    <p className="truncate text-[11.5px] leading-4 text-[var(--co-ink-4)]">To {item.recipientsLabel}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => onUndo(item.operationId)}
                    disabled={!canUndo}
                    aria-label={`Undo sending the email to ${item.recipientsLabel}`}
                    className={clsx(
                      'h-8 flex-none border px-3.5 text-[12.5px] font-semibold transition-colors duration-[120ms]',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--co-brass)] focus-visible:ring-offset-1',
                      canUndo
                        ? 'border-[var(--co-ink)] bg-[var(--co-ink)] text-[var(--co-ink-invert)] hover:opacity-90'
                        : 'cursor-not-allowed border-[var(--co-line)] bg-transparent text-[var(--co-ink-4)]',
                    )}
                  >
                    Undo
                  </button>
                </div>

                {/* Remaining time, as a shrinking line along the bottom edge. */}
                <div
                  aria-hidden
                  className="absolute bottom-0 left-0 h-[2px] bg-[var(--co-brass)] transition-[width] duration-200 ease-linear"
                  style={{ width: `${fraction * 100}%` }}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default UndoSendBar;
